<script setup lang="ts">
import { UiButton } from '#components'
import { upperFirst } from 'scule'

const { t, locale, locales, setLocale } = useI18n()
const switchLocalePath = useSwitchLocalePath()
const localeObject = useI18nLocaleObject()

const availableLocales = computed(() => locales.value.map((l: any) => {
  const formattedNumber = new Intl.NumberFormat(l.language, {
    style: 'currency',
    currency: l.currency,
  }).format(0)

  const localCurrencySymbol = formattedNumber.replace(/[\d.,\s]/g, '')

  return {
    ...l,
    label: `${t(`country.${l.country.toLowerCase()}`)} (${localCurrencySymbol})`,
    shortLabel: `${l.country} (${localCurrencySymbol})`,
    nativeName: upperFirst(l.nativeName),
  }
}))

const selectedLocale = ref(locale.value)
const localeFlagMap = locales.value.reduce((acc, cur) => {
  acc[cur.code] = cur.flag
  return acc
}, {} as Record<string, string>)

async function handleClick(l: typeof locale.value) {
  await setLocale(l)
  switchLocalePath(l)
  window.location.reload()
}
</script>

<template>
  <UiPopover>
    <UiPopoverTrigger as-child>
      <UiButton
        id="languageCountrySwitch"
        data-testid="languageCountrySwitch"
        :aria-label="t('general.language_and_currency.label')"
        variant="ghost"
        class="text-primary fw600"
      >
        <Icon
          :name="localeFlagMap[selectedLocale]!"
          :alt="t('country.flag', [t(`country.${localeObject.country.toLowerCase()}`)])"
          data-testid="languageCountrySwitchSelectedCountryFlag"
          class="mr-1 rd-full ring-0.5 ring-primary/25"
        />
        <span :data-testid="`languageCountrySwitchSelectedCountry-${localeObject.country}` ">
          {{ availableLocales.find((l) => l.code === selectedLocale)?.shortLabel }}
        </span>
      </UiButton>
    </UiPopoverTrigger>
    <UiPopoverContent
      class="w-88 px-0 py-5"
      avoid-collisions
      align="end"
      side="bottom"
    >
      <div class="mb4 wfull flex items-center gap-1 px4">
        <Icon name="lucide:globe" />
        <span class="mb-0.5 text-base text-primary fw600 leading-none">
          {{ t('region.label') }}
        </span>
      </div>
      <div data-testid="languageCountrySwitchFlyoutCountriesList" class="max-h-63.5 of-x-hidden of-y-auto">
        <div class="wfull flex flex-col">
          <div
            v-for="loc in availableLocales" :key="loc.code"
            class="relative flex cursor-pointer select-none items-center gap-2 border-t border-t-border rounded-sm px-4 py-2 text-sm fw600 outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-accent hover:text-accent-foreground"
            @click="handleClick(loc.code)"
          >
            <Icon :name="localeFlagMap[loc.code]!" alt="flag" class="mr1 rd-full ring-0.5 ring-primary/25" />
            <span data-testid="languageCountrySwitchCountryLabel">

              {{ loc.label }}
            </span>
          </div>
        </div>
      </div>
    </UiPopoverContent>
  </UiPopover>
</template>
